import { Box, Typography } from "@mui/material";
import { useState } from "react";
import { Datagrid, FunctionField, List, useLocaleState, useRecordContext, useTranslate } from "react-admin";

import { ROWS_PER_PAGE } from "../constants";
import { formatDateTime } from "../lib/dateUtils";
import { space } from "../theme/designTokens";
import { NoteType } from "../types/note";
import { ActivityTypeField } from "./ActivityTypeField";
import { LocalizedDateField } from "./fields/LocalizedDateField";
import { InfoDialog } from "./InfoDialog";
import { PractitionerRecord } from "./PractitionerShow";

export const NotesList = () => {
  const record = useRecordContext<PractitionerRecord>();
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const [modalState, setModalState] = useState<{ title: string; content: string } | null>(null);

  if (!record) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate("notesList.noDataAvailable")}
      </Typography>
    );
  }

  return (
    <List
      resource="PractitionerNotes"
      filter={{ userId: record.userId }}
      actions={false}
      perPage={ROWS_PER_PAGE}
      title={translate("notesList.title")}
      empty={<Typography variant="body1">{translate("notesList.noNotes")}</Typography>}
    >
      <Datagrid
        bulkActionButtons={false}
        rowClick={(_id, _basePath, record) => {
          const note = record as NoteType;
          const { date: formattedDate, time: formattedTime } = formatDateTime(note.updatedAt, locale);
          const title = translate("noteModal.title", {
            author: note.authorName,
            date: formattedDate,
            time: formattedTime,
          });
          setModalState({ title, content: note.note });
          return false;
        }}
      >
        <FunctionField
          label={translate("notesList.note")}
          render={(note: NoteType) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: space.xxs }}>
              {note.activityType && <ActivityTypeField />}
              <span>{note.note}</span>
            </Box>
          )}
        />
        <LocalizedDateField
          source="updatedAt"
          label={translate("notesList.date")}
          options={{
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }}
        />
        <LocalizedDateField
          source="updatedAt"
          label={translate("notesList.time")}
          showTime
          showDate={false}
          options={{
            hour: "2-digit",
            minute: "2-digit",
          }}
        />
      </Datagrid>
      {modalState && (
        <InfoDialog title={modalState.title} content={modalState.content} onClose={() => setModalState(null)} />
      )}
    </List>
  );
};
